/**
 * Thread (conversation) management.
 *
 * Threads are LangGraph checkpoints in PostgreSQL. We list them via
 * `GET /v1/threads` and fetch their full message history via
 * `GET /v1/history/{thread_id}`.
 */

import { ref } from 'vue'
import { apiGet, apiDelete, apiPatch } from '@/services/api'
import type { ThreadSummary, HistoryResponse } from '@/types'

// Persist the active thread so a page refresh returns the user to the same
// conversation (and lets us reconnect to an in-flight run) instead of dumping
// them on the welcome screen.
const ACTIVE_THREAD_STORAGE_KEY = 'jarvis.activeThreadId'

function readStoredThreadId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_THREAD_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredThreadId(threadId: string | null): void {
  try {
    if (threadId) localStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, threadId)
    else localStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY)
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}

const threads = ref<ThreadSummary[]>([])
const activeThreadId = ref<string | null>(null)
const loadingThreads = ref(false)
const loadingHistory = ref(false)

export function useThreads() {
  async function refreshThreads(limit = 50): Promise<void> {
    loadingThreads.value = true
    try {
      const data = await apiGet<ThreadSummary[]>(`/v1/threads?limit=${limit}`)
      threads.value = data
    } catch {
      // Surfaced via the centralized 401 handler when applicable; otherwise
      // we just leave the list empty until the user retries.
    } finally {
      loadingThreads.value = false
    }
  }

  async function fetchHistory(threadId: string): Promise<HistoryResponse | null> {
    loadingHistory.value = true
    try {
      return await apiGet<HistoryResponse>(`/v1/history/${encodeURIComponent(threadId)}`)
    } catch {
      return null
    } finally {
      loadingHistory.value = false
    }
  }

  function selectThread(threadId: string | null) {
    activeThreadId.value = threadId
    writeStoredThreadId(threadId)
  }

  function newThread() {
    activeThreadId.value = null
    writeStoredThreadId(null)
  }

  function getStoredThreadId(): string | null {
    return readStoredThreadId()
  }

  /**
   * Called by useChat after a successful chat round so the sidebar shows
   * the freshly-created thread without waiting for a manual refresh.
   */
  function upsertThread(summary: ThreadSummary) {
    const idx = threads.value.findIndex(t => t.thread_id === summary.thread_id)
    if (idx >= 0) {
      threads.value.splice(idx, 1, { ...threads.value[idx], ...summary })
    } else {
      threads.value.unshift(summary)
    }
  }

  /** Permanently delete a thread; drops it locally and resets to a new chat if active. */
  async function deleteThread(threadId: string): Promise<void> {
    await apiDelete(`/v1/threads/${encodeURIComponent(threadId)}`)
    threads.value = threads.value.filter(t => t.thread_id !== threadId)
    if (activeThreadId.value === threadId) newThread()
  }

  /** Rename a thread and reflect the new title locally. */
  async function renameThread(threadId: string, name: string): Promise<void> {
    const trimmed = name.trim()
    if (!trimmed) return
    await apiPatch(`/v1/threads/${encodeURIComponent(threadId)}`, { name: trimmed })
    const idx = threads.value.findIndex(t => t.thread_id === threadId)
    if (idx >= 0) {
      threads.value.splice(idx, 1, { ...threads.value[idx], name: trimmed })
    }
  }

  return {
    threads,
    activeThreadId,
    loadingThreads,
    loadingHistory,
    refreshThreads,
    fetchHistory,
    selectThread,
    newThread,
    upsertThread,
    getStoredThreadId,
    deleteThread,
    renameThread,
  }
}
